import { Typography } from '@mui/material';
import AudioMotionAnalyzer, { ConstructorOptions } from 'audiomotion-analyzer';
import { RemoteAudioTrack, Track } from 'livekit-client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';

const options: ConstructorOptions = {
  alphaBars: true,
  ansiBands: false,
  barSpace: 0.5,
  bgAlpha: 0,
  channelLayout: 'single',
  colorMode: 'bar-level',
  connectSpeakers: true,
  fadePeaks: false,
  fftSize: 8192,
  fillAlpha: 0.2,
  frequencyScale: 'log',
  gradient: 'prism',
  gravity: 3.8,
  height: 60,
  ledBars: false,
  linearAmplitude: true,
  linearBoost: 1.6,
  lineWidth: 2,
  loRes: false,
  lumiBars: false,
  maxDecibels: -35,
  maxFPS: 0,
  maxFreq: 16000,
  minDecibels: -85,
  minFreq: 30,
  mirror: 0,
  mode: 7,
  noteLabels: false,
  outlineBars: true,
  overlay: true,
  peakFadeTime: 750,
  peakHoldTime: 500,
  peakLine: false,
  radial: false,
  radialInvert: false,
  radius: 0.3,
  reflexAlpha: 1,
  reflexBright: 1,
  reflexFit: true,
  reflexRatio: 0.5,
  roundBars: true,
  showBgColor: true,
  showFPS: false,
  showPeaks: false,
  showScaleX: false,
  showScaleY: false,
  smoothing: 0.7,
  spinSpeed: 1,
  splitGradient: true,
  start: true,
  trueLeds: true,
  useCanvas: true,
  volume: 1,
  weightingFilter: 'D',
};

export const AITalk = () => {
  const [audioMotion, setAudioMotion] = useState<AudioMotionAnalyzer>();
  const talkRef = useRef<HTMLDivElement>(null);

  const { room, remoteParticipants } = useLivekitRTCState();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const aiParticipantTrack = useMemo(() => {
    return Array.from(
      room?.remoteParticipants?.values() || [],
    )[0]?.getTrackPublication(Track.Source.Microphone)
      ?.audioTrack as RemoteAudioTrack;
  }, [room?.remoteParticipants, remoteParticipants]);

  useEffect(() => {
    if (!talkRef.current || audioMotion || !aiParticipantTrack?.mediaStream)
      return;

    setAudioMotion(new AudioMotionAnalyzer(talkRef.current, options));
  }, [aiParticipantTrack, audioMotion]);

  useEffect(() => {
    if (!audioMotion || !aiParticipantTrack.mediaStream) return;

    const micStream = audioMotion.audioCtx.createMediaStreamSource(
      aiParticipantTrack.mediaStream,
    );
    // connect microphone stream to analyzer
    audioMotion.connectInput(micStream);
    // mute output to prevent feedback loops from the speakers
    audioMotion.volume = 0;
  }, [audioMotion, aiParticipantTrack]);

  return (
    <div
      style={{ display: 'flex', minHeight: '60px', alignItems: 'center' }}
      ref={talkRef}
    >
      {!aiParticipantTrack && (
        <Typography variant="caption" color="text.secondary">
          Waiting for AI to join...
        </Typography>
      )}
    </div>
  );
};
